<script setup lang="ts">
import BackstageIcon from '@/components/common/BackstageIcon.vue';
import { ButtonVariant } from '@/models/button-variant.enum';
import type { IconValues } from '@/models/icons.enum';
import { computed, useSlots } from 'vue';

export interface Props {
  iconLeft?: IconValues;
  iconRight?: IconValues;
  variant?: ButtonVariant;
  fullWidth?: boolean;
  disabled?: boolean;
  dataCyButton?: string;
}

const props = withDefaults(defineProps<Props>(), {
  fullWidth: false,
  disabled: false,
  variant: ButtonVariant.PRIMARY,
  dataCyButton: '',
});

const slots = useSlots();

const variantClass = computed(() => props.variant ?? ButtonVariant.PRIMARY);

const isIconButton = computed(() => {
  const hasIcon = !!props.iconLeft || !!props.iconRight;
  const hasText = !!slots.default?.() && !!slots.default?.()[0].el;
  const hasChildren = !!slots.default && slots.default()?.[0].children;

  return hasIcon && !hasText && !hasChildren;
});
</script>

<template>
  <div class="button-container">
    <button
      v-bind="$attrs"
      :disabled="disabled"
      :data-cy="dataCyButton"
      v-on:click.stop="$event.stopPropagation()"
      :class="[variantClass, { 'icon-button': isIconButton }, { 'full-width': fullWidth }]">
      <BackstageIcon v-if="iconLeft" :name="iconLeft" class="icon left" />
      <slot> </slot>
      <BackstageIcon v-if="iconRight" :name="iconRight" class="icon right" />
    </button>
  </div>
</template>

<style scoped lang="scss">
.button-container {
  display: flex;

  button {
    display: flex;
    align-items: center;
    padding: 10.5px 28px;
    background-color: var(--primary-button-bg-color);
    color: var(--secondary-text-color);
    border-radius: 100px;
    border: none;
    min-width: unset;
    cursor: pointer;
    word-break: initial;
    height: 42px;
    font-size: 0.875rem;

    &:has(.icon) {
      padding: 10.5px 20px;
    }

    .icon {
      &.left {
        margin-right: 0.5rem;
      }

      &.right {
        margin-left: 0.5rem;
      }
    }

    &.primary {
      &:hover:not(:disabled) {
        background-color: var(--secondary-color);
      }

      &:active:not(:disabled) {
        background-color: var(--primary-active-button-color);
        outline: none;
      }
    }

    &.secondary {
      background-color: transparent;
      border: 1px solid var(--primary-text-color);
      color: var(--primary-text-color);

      &:hover:not(:disabled) {
        background-color: var(--bg-remark-selected-color);
      }

      &:focus {
        outline: 0;
      }

      &:active:not(:disabled) {
        background-color: var(--gray-color);
        outline: none;
      }
    }

    &:disabled,
    .disabled {
      opacity: 0.2;
      cursor: not-allowed;
    }

    &.icon-button {
      width: 42px;
      padding: 0;
      justify-content: center;

      .icon {
        margin: 0;
      }
    }

    &.pale-pinkish {
      background-color: var(--pale-pinkish-color);
      color: var(--primary-text-color);
      border: none;
    }
  }

  .full-width {
    width: 100%;
    justify-content: center;
  }

  .full-width {
    width: 100%;
    justify-content: center;
  }

  &.submitting button,
  button.submitting {
    background-image: url('@/assets/img/cloud-loader.webp');
    background-size: 30px;
    background-repeat: no-repeat;
    background-position: 90% center;
    padding-right: 60px;
    opacity: 0.6;
    cursor: initial;

    &.centered {
      background-position: center;
    }
  }
}
</style>
